/**
 * Helper functions to normalize lead capture field data structure
 * Handles migration from old schema (options at root) to new schema (config object)
 */

/**
 * Normalizes a field to use the new config-based structure
 * Supports backward compatibility with old schema
 * @param {Object} field - The field object (may have old or new structure)
 * @returns {Object} - Normalized field with config object
 */
export const normalizeField = (field) => {
    if (!field) return field;

    const normalized = {
        ...field,
        type: field.type || 'text',
        config: field.config || {}
    };

    // Migrate old `options` at root level to `config.options`
    if (field.options && !normalized.config.options) {
        normalized.config.options = field.options;
    }

    // Ensure config exists for dropdown/radio/multiselect fields
    if ((field.type === 'dropdown' || field.type === 'radio' || field.type === 'multiselect') && !normalized.config.options) {
        normalized.config.options = [];
    }

    // Remove old `options` from root if it exists (after migration)
    if (normalized.options && normalized.config.options) {
        delete normalized.options;
    }

    return normalized;
};

/**
 * Normalizes an array of fields
 * @param {Array} fields - Array of field objects
 * @returns {Array} - Array of normalized fields
 */
export const normalizeFields = (fields) => {
    if (!Array.isArray(fields)) return fields;
    return fields.map(normalizeField);
};

/**
 * Gets options from a field (supports both old and new structure)
 * @param {Object} field - The field object
 * @returns {Array} - Array of options or empty array
 */
export const getFieldOptions = (field) => {
    if (!field) return [];
    // Check new structure first
    if (field.config && field.config.options) {
        return field.config.options;
    }
    // Fallback to old structure for backward compatibility
    if (field.options) {
        return field.options;
    }
    return [];
};

/**
 * Sets options for a field (always uses new config structure)
 * @param {Object} field - The field object
 * @param {Array} options - Array of option strings
 * @returns {Object} - Updated field with options in config
 */
export const setFieldOptions = (field, options) => {
    if (!field) return field;
    return {
        ...field,
        config: {
            ...(field.config || {}),
            options: options || []
        }
    };
};

